import { captureMagazinePage } from "../features/magazine/_measurement";
import { initializeNoroshiReserveBridge } from "./lib/noroshi-reserve-bridge";
import React, { Component, useState } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { createInertiaApp, router } from "@inertiajs/react";
import { createRoot } from "react-dom/client";
import { pageTitle } from "./page_title";
import PublicPageHead from "./public_page_head";
import SalonpagePrivacy from "../features/salonpage/privacy";
import SalonpageStaffs from "../features/salonpage/staffs";
import SalonpageTerms from "../features/salonpage/terms";

const pages = {
  ...import.meta.glob(
    ["../features/**/*.tsx", "!../features/**/*.test.tsx"],
    { eager: true },
  ),
  "salonpage/privacy.tsx": { default: SalonpagePrivacy },
  "salonpage/staffs.tsx": { default: SalonpageStaffs },
  "salonpage/terms.tsx": { default: SalonpageTerms },
} as Record<
  string,
  { default: React.ComponentType }
>;

if (Object.keys(pages).length === 0) {
  console.error("Vite Glob found NO pages! Check path: ./pages/**/*.tsx");
}

// =====================================================================================
// ErrorBoundary
// =====================================================================================

interface EBProps { children: ReactNode }
interface EBState { hasError: boolean; error: Error | null; errorInfo: ErrorInfo | null }

class ErrorBoundary extends Component<EBProps, EBState> {
  state: EBState = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<EBState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.group("React Error");
    console.error("Error:", error.message);
    console.error("Stack:", error.stack);
    console.error("Component Stack:", errorInfo.componentStack);
    console.groupEnd();

    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <ErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}

// =====================================================================================
// ErrorDisplay
// =====================================================================================

function ErrorDisplay({ error, errorInfo, onReset }: { error: Error; errorInfo: ErrorInfo | null; onReset?: () => void }) {
  const [copied, setCopied] = useState(false);
  let version: string | undefined;
  try {
    const dataPage = document.querySelector("[data-page]");
    if (dataPage?.textContent) {
      version = JSON.parse(dataPage.textContent).version;
    }
  } catch { /* noop */ }

  const report = [
    "## React Error Details",
    "",
    "### Error Message",
    "```",
    error.toString(),
    "```",
    "",
    "### Stack Trace",
    "```",
    error.stack || "",
    "```",
    "",
    "### Component Stack",
    "```",
    errorInfo?.componentStack || "Not available",
    "```",
    "",
    "### Environment",
    `- URL: ${window.location.href}`,
    `- Timestamp: ${new Date().toISOString()}`,
    `- User Agent: ${navigator.userAgent}`,
    `- Version: ${version || "unknown"}`,
  ].join("\n");

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-white via-[#f8f7f2] to-[#eef3f0] text-gray-900">
      <div className="mx-auto flex min-h-screen max-w-2xl items-center px-6 py-16">
        <section className="relative w-full rounded-3xl border border-gray-200/80 bg-white/90 p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur">
          {version && (
            <span className="absolute right-6 top-6 text-[10px] text-gray-300">
              {version.slice(0, 12)}
            </span>
          )}
          <p className="text-xs uppercase tracking-[0.3em] text-gray-400">
            Development Error
          </p>
          <h1 className="mt-3 text-2xl font-semibold leading-tight text-gray-900 sm:text-3xl">
            {error.name}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
            {error.message}
          </p>
          <div className="mt-8 h-px w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
          <div className="mt-6 text-sm text-gray-500">
            状況が続く場合は、詳細をコピーして共有してください。
          </div>

          <details
            open
            className="mt-6 rounded-2xl border border-gray-200 bg-gray-50/80 p-4 text-xs text-gray-700"
          >
            <summary className="cursor-pointer text-sm font-semibold text-gray-800">
              詳細を表示
            </summary>
            <div className="mt-3 space-y-3">
              <div className="flex justify-end gap-2">
                {onReset && (
                  <button
                    onClick={onReset}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                  >
                    リセット
                  </button>
                )}
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                >
                  {copied ? (
                    <>
                      <span className="text-green-600">&#10003;</span>
                      コピー完了
                    </>
                  ) : (
                    <>コピー</>
                  )}
                </button>
              </div>

              <pre className="whitespace-pre-wrap rounded-lg bg-white/80 p-3 text-[11px] leading-relaxed text-red-700">
                {error.toString()}
              </pre>

              {error.stack && (
                <pre className="whitespace-pre-wrap rounded-lg bg-white/80 p-3 text-[11px] leading-relaxed text-gray-600">
                  {error.stack}
                </pre>
              )}

              {errorInfo?.componentStack && (
                <pre className="whitespace-pre-wrap rounded-lg bg-white/80 p-3 text-[11px] leading-relaxed text-gray-600">
                  {errorInfo.componentStack}
                </pre>
              )}
            </div>
          </details>
        </section>
      </div>
    </main>
  );
}

// =====================================================================================
// App Bootstrap
// =====================================================================================

export default () => {
  const appElement = document.getElementById("app");
  if (!appElement) return;

  initializeNoroshiReserveBridge();
  const capturePage = () => {
    captureMagazinePage();
  };
  capturePage();
  router.on("navigate", capturePage);

  createInertiaApp({
    resolve: (name) => {
      const page = Object.entries(pages).find(([path]) =>
        path.endsWith(`${name}.tsx`)
      )?.[1];
      if (!page) {
        throw new Error(`Page not found: ${name}`);
      }
      const Page = page.default as React.ComponentType<Record<string, unknown>>;
      return function PublicPage(props: Record<string, unknown> & { publicMetadata?: React.ComponentProps<typeof PublicPageHead>["metadata"] }) {
        return <>
          <PublicPageHead component={name} metadata={props.publicMetadata} />
          <Page {...props} />
        </>;
      };
    },
    title: (title) => pageTitle(title, window.location.hostname),
    setup({ el, App, props }) {
      createRoot(el).render(
        <ErrorBoundary>
          <App {...props} />
        </ErrorBoundary>
      );
    },
  });
};
